// Avviso "pagamenti arretrati": mesi passati in cui qualcosa di dovuto non è
// stato registrato nei Pagamenti. Lo calcola il backend (backend/stato_pagamenti.py)
// e arriva come `stato_pagamenti.arretrati` su lista clienti e scheda cliente.
// Resta visibile finché tutto lo storico non è allineato, anche se i mesi
// successivi sono pagati: il mese corrente ha già la sua etichetta Pagato.
//
// MesiArretrati sono le righe per registrarli, con il dettaglio di
// `arretrati_dettaglio` (stato_pagamenti.dettaglio_arretrati): le usano la
// sezione Pagamenti della scheda e la tab Recuperi Pagamenti dei Compensi.
import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowDown, CheckCircle2, Loader2, Pencil, RefreshCw } from "lucide-react";
import { Input } from "./ui/input";
import { apiFetch } from "../utils/api";
import { formatCurrency } from "../utils/formatters";
import { PRIMARY_BTN_SM, SECONDARY_BTN, FIELD_CLS, InfoTooltip, DatePicker, NumberField } from "./designSystem";

const maiuscola = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const euro = (v) => formatCurrency(v, { decimals: Number(v) % 1 ? 2 : 0 });

function descrizione(arretrati) {
  return arretrati.map((a) => `${maiuscola(a.nome_mese)}: ${a.voci.join(", ")}`).join(" · ");
}

// Chip compatta per la lista clienti, con il dettaglio nel tooltip.
export function BadgeArretrati({ arretrati, testId }) {
  if (!arretrati?.length) return null;
  const n = arretrati.length;
  return (
    <InfoTooltip
      title={n === 1 ? `Arretrato: ${arretrati[0].nome_mese}` : `${n} mesi arretrati`}
      description={`${descrizione(arretrati)}. Resta finché lo storico dei pagamenti non è allineato.`}
    >
      <span
        className="h-[22px] px-2 rounded-full bg-[#fdecea] text-[#c0392b] border border-[#f6c9c3] text-[11.5px] font-semibold inline-flex items-center gap-1 whitespace-nowrap"
        data-testid={testId}
      >
        <AlertTriangle className="w-3 h-3" strokeWidth={2.2} />
        {n === 1 ? "Arretrato" : `${n} mesi arretrati`}
      </span>
    </InfoTooltip>
  );
}

// Contratto scaduto e non ancora rinnovato (`stato_pagamenti.da_rinnovare`,
// backend/stato_pagamenti.py): senza voci dovute il cliente risulterebbe
// pagato, il backend lo tiene non pagato e qui deve saltare all'occhio.
export function BadgeDaRinnovare({ scadenza, testId }) {
  const data = scadenza ? new Date(scadenza).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : null;
  return (
    <InfoTooltip
      title="Contratto da rinnovare"
      description={`${data ? `Il contratto è scaduto il ${data}` : "Il contratto è scaduto"} e non è stato rinnovato: il cliente resta non pagato finché non si firma un rinnovo.`}
    >
      <span
        className="h-[22px] px-2 rounded-full bg-[#c0392b] text-white text-[11.5px] font-semibold inline-flex items-center gap-1 whitespace-nowrap"
        data-testid={testId}
      >
        <RefreshCw className="w-3 h-3" strokeWidth={2.4} />
        Da rinnovare
      </span>
    </InfoTooltip>
  );
}

// Banner per la scheda cliente: un mese per riga con le voci non registrate.
export function AvvisoArretrati({ arretrati, onVaiAiPagamenti }) {
  if (!arretrati?.length) return null;
  const n = arretrati.length;
  return (
    <div
      className="flex items-start gap-3 px-5 py-4 rounded-[16px] bg-[#fef6f5] border border-[#f6c9c3] border-l-[3px] border-l-[#c0392b] scheda-font-ui"
      role="alert"
      data-testid="avviso-arretrati"
    >
      <AlertTriangle className="w-[18px] h-[18px] text-[#c0392b] shrink-0 mt-0.5" strokeWidth={2} />
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <span className="text-[14.5px] font-bold text-[#8f2a1f]">
          {n === 1 ? `Pagamenti non registrati per ${arretrati[0].nome_mese}` : `Pagamenti non registrati in ${n} mesi passati`}
        </span>
        <ul className="flex flex-col gap-0.5 text-[13px] text-[#8f2a1f]">
          {arretrati.map((a) => (
            <li key={a.mese}>
              <b className="font-semibold">{maiuscola(a.nome_mese)}</b>: {a.voci.join(", ")}
            </li>
          ))}
        </ul>
        <span className="text-[12px] text-[#a0564c]">
          L&apos;avviso resta finché tutto lo storico dei pagamenti non è allineato.
        </span>
      </div>
      {onVaiAiPagamenti && (
        <button
          type="button"
          onClick={onVaiAiPagamenti}
          className="h-8 px-3 rounded-[10px] border border-[#f0d6d2] bg-white text-[12.5px] font-semibold text-[#c0392b] hover:bg-[#fdecea] transition-colors inline-flex items-center gap-1 shrink-0"
        >
          <ArrowDown className="w-3.5 h-3.5" /> Vai ai pagamenti
        </button>
      )}
    </div>
  );
}

// Le voci scoperte di un mese diventano le registrazioni da fare: i mensili in
// un'unica fattura (POST /pagamenti/mensili, come il canone in scheda), le una
// tantum una per voce.
function registrazioniDelMese(mese) {
  const mensili = mese.voci.filter((v) => v.tipo === "mensile");
  const righe = [];
  if (mensili.length) {
    const noti = mensili.filter((v) => v.importo != null);
    righe.push({
      key: `canone_${mese.mese}`,
      label: "Canone mensile",
      dettaglio: mensili.map((v) => v.label).join(" · "),
      importo: noti.length ? noti.reduce((s, v) => s + v.importo, 0) : null,
      parziale: noti.length < mensili.length,
      canone: true,
    });
  }
  mese.voci.filter((v) => v.tipo !== "mensile").forEach((v, i) => {
    righe.push({
      key: `${v.tipo}_${v.pacchetto_id || ""}_${v.addon_id || i}_${mese.mese}`,
      label: v.label,
      dettaglio: v.tipo === "attivazione" ? "Attivazione" : "Una tantum",
      importo: v.importo,
      parziale: v.importo == null,
      voce: v,
    });
  });
  return righe;
}

function RigaRegistrazione({ riga, mese, clientId, canRegister, onRegistrato }) {
  const oggi = new Date().toISOString().slice(0, 10);
  const [aperto, setAperto] = useState(false);
  const [form, setForm] = useState({ importo: riga.importo ?? "", data_pagamento: oggi, note: "" });
  const [saving, setSaving] = useState(false);

  const registra = async (dati) => {
    if (dati.importo === "" || dati.importo == null || isNaN(Number(dati.importo)) || Number(dati.importo) < 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    setSaving(true);
    try {
      const comuni = { importo: Number(dati.importo), data_pagamento: dati.data_pagamento || oggi, note: dati.note || null };
      const v = riga.voce;
      const body = riga.canone
        ? { ...comuni, periodo: mese.mese }
        : v.tipo === "attivazione"
          ? { ...comuni, tipo: "attivazione", label: v.label, ricorrenza: "una_tantum", pacchetto_id: v.pacchetto_id }
          : { ...comuni, tipo: "addon", label: v.label, ricorrenza: "una_tantum", addon_id: v.addon_id };
      const res = await apiFetch(`/clients/${clientId}/pagamenti${riga.canone ? "/mensili" : ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const risposta = await res.json().catch(() => ({}));
        setAperto(false);
        onRegistrato(risposta, mese);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nella registrazione");
      }
    } finally {
      setSaving(false);
    }
  };

  // Come la registrazione rapida in scheda: con una quota a percentuale il
  // bottone propone la sola parte fissa, la matita serve per l'importo fatturato.
  const rapido = canRegister && riga.importo > 0;
  return (
    <div className="px-4 py-3" data-testid={`arretrato-riga-${riga.key}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="w-2 h-2 rounded-full bg-[#e05a4a] shrink-0" />
        <div className="flex-1 min-w-[160px]">
          <div className="text-[13.5px] font-semibold text-[#15161c]">{riga.label}</div>
          <div className="text-[12px] text-[#9095a1]">{riga.dettaglio}</div>
        </div>
        <span className="text-[13.5px] font-semibold scheda-font-mono text-[#15161c] shrink-0">
          {riga.importo != null ? euro(riga.importo) : "—"}
          {riga.parziale && riga.importo != null && <span className="text-[#9095a1]"> + %</span>}
        </span>
        {canRegister && (rapido && !aperto ? (
          <div className="flex items-center gap-1.5">
            <button
              className={PRIMARY_BTN_SM}
              onClick={() => registra({ importo: riga.importo, data_pagamento: oggi, note: "" })}
              disabled={saving}
              data-testid={`arretrato-registra-${riga.key}`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Registra {euro(riga.importo)}
            </button>
            <InfoTooltip title="Registra con altri dati" description="Importo diverso, data del pagamento o note.">
              <button
                className={`${SECONDARY_BTN} !px-0 w-8 justify-center`}
                onClick={() => setAperto(true)}
                disabled={saving}
                aria-label="Registra con altri dati"
                data-testid={`arretrato-modifica-${riga.key}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </InfoTooltip>
          </div>
        ) : (
          <button className={SECONDARY_BTN} onClick={() => setAperto((a) => !a)} data-testid={`arretrato-modifica-${riga.key}`}>
            {aperto ? "Annulla" : "+ Registra"}
          </button>
        ))}
      </div>
      {aperto && (
        <div className="mt-2.5 ml-5 p-3 rounded-[12px] bg-[#fbfbfd] border border-[#eeeef5] flex flex-wrap items-end gap-2">
          <div>
            <label className="text-[11px] text-[#9095a1]">{riga.canone ? "Importo fattura" : "Importo"}</label>
            <div className="mt-1 w-32">
              <NumberField value={form.importo} onChange={(v) => setForm((f) => ({ ...f, importo: v }))} testId="arretrato-importo" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-[#9095a1]">Data pagamento</label>
            <DatePicker
              value={form.data_pagamento}
              onChange={(v) => setForm((f) => ({ ...f, data_pagamento: v }))}
              className={`${FIELD_CLS} h-[38px] mt-1 w-36`}
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-[11px] text-[#9095a1]">Note (opzionale)</label>
            <Input
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              className={`${FIELD_CLS} h-[38px] mt-1`}
            />
          </div>
          <button className={PRIMARY_BTN_SM} style={{ height: 38 }} onClick={() => registra(form)} disabled={saving} data-testid="arretrato-conferma">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Conferma
          </button>
        </div>
      )}
    </div>
  );
}

// Un blocco per mese arretrato, con le sue righe da registrare.
export function MesiArretrati({ mesi, clientId, canRegister, onRegistrato }) {
  return mesi.map((m) => (
    <div key={m.mese} className="border-b border-[#f0f0f6] last:border-b-0" data-testid={`arretrato-mese-${m.mese}`}>
      <div className="px-4 pt-3 text-[10.5px] font-bold uppercase tracking-[.08em] text-[#9095a1]">
        {m.nome_mese}
      </div>
      <div className="divide-y divide-[#f5f5f9]">
        {registrazioniDelMese(m).map((r) => (
          <RigaRegistrazione key={r.key} riga={r} mese={m} clientId={clientId} canRegister={canRegister} onRegistrato={onRegistrato} />
        ))}
      </div>
    </div>
  ));
}

// Conferma di una registrazione arretrata, con i compensi che il backend ha
// recuperato da solo (routes/compensation.py, recuperi_da_pagamento).
export function avvisaRegistrato(risposta, mese) {
  const creati = risposta.recuperi_creati || [];
  const recuperato = creati.reduce((s, r) => s + (r.total_delta || 0), 0);
  toast.success(`Pagamento registrato · ${maiuscola(mese.nome_mese)}`, {
    description: creati.length ? `Compensi recuperati: +${euro(recuperato)} nel payout di questo mese` : undefined,
  });
}
